import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import CommunityInsights from "@/components/CommunityInsights";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Download, MoreHorizontal, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, LayoutGrid, List, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import TableFilterBar from "@/components/table/TableFilterBar";
import TableSettingsGear, { TableColumnsPanel } from "@/components/table/TableSettingsGear";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { toast } from "sonner";
import MemberFormDialog from "@/components/MemberFormDialog";
import MemberDetailDialog from "@/components/MemberDetailDialog";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, Users } from "lucide-react";
import { cn, sortGeneralFirst, sortGeneralFirstByName } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

type Member = Tables<"members">;

const getMemberMinistries = (department: string | null | undefined): string[] => {
  if (!department) return [];
  return department.split(",").map((value) => value.trim()).filter(Boolean);
};

type ColumnKey = "member" | "uid" | "email" | "organization" | "phone" | "gender" | "status" | "baptized" | "date_joined" | "date_of_birth" | "family" | "address" | "city" | "state" | "zip_code" | "education_status" | "year_level" | "school_name" | "field_of_study" | "degree_earned" | "employment_status" | "occupation" | "industry" | "employer_name" | "marital_status" | "notes";

interface ColumnDef {
  key: ColumnKey;
  label: string;
  sortField: string;
  render: (m: Member) => React.ReactNode;
}

type SortDir = "asc" | "desc" | null;

const FILTER_TO_COLUMN: Record<string, ColumnKey> = {
  organization: "organization",
  status: "status",
  gender: "gender",
  baptized: "baptized",
  education: "education_status",
  year_level: "year_level",
  employment: "employment_status",
  marital: "marital_status",
};

const PEOPLE_TABLE_COLUMN_ORDER_STORAGE_KEY = "members_table_column_order";
const MEMBERS_PAGE_SIZE_STORAGE_KEY = "members_table_page_size";
const DEFAULT_COLUMN_ORDER: ColumnKey[] = [
  "member", "uid", "email", "organization", "phone", "gender", "status", "baptized", "date_joined", "date_of_birth", "family", "city", "state", "zip_code", "education_status", "employment_status", "marital_status", "address", "year_level", "school_name", "field_of_study", "degree_earned", "occupation", "industry", "employer_name", "notes",
];
const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = ["member", "uid", "organization", "phone", "gender", "status"];

interface MembersProps {
  hideTitle?: boolean;
  hideActions?: boolean;
  onAdd?: () => void;
}

const Members = ({ hideTitle = false, hideActions = false, onAdd }: MembersProps) => {
  const [search, setSearch] = useState("");
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [viewMember, setViewMember] = useState<Member | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("full_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(() => {
    if (typeof window === "undefined") return DEFAULT_COLUMN_ORDER;

    try {
      const raw = window.localStorage.getItem(PEOPLE_TABLE_COLUMN_ORDER_STORAGE_KEY);
      if (!raw) return DEFAULT_COLUMN_ORDER;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return DEFAULT_COLUMN_ORDER;

      const saved = parsed.filter((key): key is ColumnKey =>
        typeof key === "string" && DEFAULT_COLUMN_ORDER.includes(key as ColumnKey)
      );

      const missing = DEFAULT_COLUMN_ORDER.filter((key) => !saved.includes(key));
      return [...saved, ...missing];
    } catch {
      return DEFAULT_COLUMN_ORDER;
    }
  });
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(DEFAULT_VISIBLE_COLUMNS)
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [orgFilters, setOrgFilters] = useState<Set<string>>(new Set());
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [genderFilters, setGenderFilters] = useState<Set<string>>(new Set());
  const [baptizedFilters, setBaptizedFilters] = useState<Set<string>>(new Set());
  const [educationFilters, setEducationFilters] = useState<Set<string>>(new Set());
  const [yearLevelFilters, setYearLevelFilters] = useState<Set<string>>(new Set());
  const [employmentFilters, setEmploymentFilters] = useState<Set<string>>(new Set());
  const [maritalFilters, setMaritalFilters] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === "undefined") return 10;
    const raw = window.localStorage.getItem(MEMBERS_PAGE_SIZE_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return [10, 20, 50, 100].includes(parsed) ? parsed : 10;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleOrgFilter = (val: string) => { setOrgFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleStatusFilter = (val: string) => { setStatusFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleGenderFilter = (val: string) => { setGenderFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleBaptizedFilter = (val: string) => { setBaptizedFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleEducationFilter = (val: string) => { setEducationFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleYearLevelFilter = (val: string) => { setYearLevelFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleEmploymentFilter = (val: string) => { setEmploymentFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleMaritalFilter = (val: string) => { setMaritalFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };

  useEffect(() => {
    if (!visibleColumns.has(FILTER_TO_COLUMN.organization) && orgFilters.size > 0) setOrgFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.status) && statusFilters.size > 0) setStatusFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.gender) && genderFilters.size > 0) setGenderFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.baptized) && baptizedFilters.size > 0) setBaptizedFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.education) && educationFilters.size > 0) setEducationFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.year_level) && yearLevelFilters.size > 0) setYearLevelFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.employment) && employmentFilters.size > 0) setEmploymentFilters(new Set());
    if (!visibleColumns.has(FILTER_TO_COLUMN.marital) && maritalFilters.size > 0) setMaritalFilters(new Set());
  }, [visibleColumns, orgFilters, statusFilters, genderFilters, baptizedFilters, educationFilters, yearLevelFilters, employmentFilters, maritalFilters]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PEOPLE_TABLE_COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(MEMBERS_PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { getPeopleOrganizationIds, hasPeopleScopedAccess } = usePermissions();
  const { memberCount, memberLimit, isAtMemberLimit } = useSubscription();
  const { requireSubscription, isPreview } = useFreemium();
  const memberUsagePercent = memberLimit ? Math.min(100, (memberCount / memberLimit) * 100) : 0;
  const allowedPeopleOrgIds = getPeopleOrganizationIds();
  const peopleScoped = role !== "admin" && hasPeopleScopedAccess();
  const getMemberDisplayName = (member: Member) => {
    const full = (member.full_name ?? "").trim();
    if (full) return full;
    const fallback = [member.first_name, member.middle_name, member.last_name]
      .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
      .join(" ")
      .trim();
    return fallback || "Unnamed Member";
  };

  const getMemberInitials = (member: Member) => {
    const name = getMemberDisplayName(member);
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0]?.toUpperCase())
      .join("")
      .slice(0, 2);
  };

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("members").select("*").order("full_name");
      if (error) throw error;
      return data as Member[];
    },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase.from("organizations").select("id, name, type").order("type").order("name");
      if (error) throw error;
      return sortGeneralFirstByName(data || []);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("members").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_all_members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_member_count"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_families"] });
      toast.success("Member deleted");
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const allowedMinistryNames = useMemo(() => {
    if (!peopleScoped) return null;
    const names = organizations
      .filter((org: any) => allowedPeopleOrgIds.includes(org.id))
      .map((org: any) => org.name)
      .filter(Boolean);
    return new Set(names);
  }, [peopleScoped, organizations, allowedPeopleOrgIds]);

  const scopedMembers = useMemo(() => {
    if (!peopleScoped || !allowedMinistryNames) return members;
    return members.filter((member) => {
      const ministries = getMemberMinistries(member.department);
      return ministries.some((name) => allowedMinistryNames.has(name));
    });
  }, [members, peopleScoped, allowedMinistryNames]);

  const filtered = useMemo(() => {
    let result = scopedMembers.filter((m) => {
      const name = getMemberDisplayName(m).toLowerCase();
      const query = search.toLowerCase();
      const matchesSearch =
        name.includes(query) ||
        (m.department?.toLowerCase().includes(query) ?? false) ||
        (m.email?.toLowerCase().includes(query) ?? false);
      const memberMinistries = getMemberMinistries(m.department);
      const matchesOrg = orgFilters.size === 0 || memberMinistries.some((ministry) => orgFilters.has(ministry));
      const matchesStatus = statusFilters.size === 0 || statusFilters.has(m.status || "—");
      const matchesGender = genderFilters.size === 0 || genderFilters.has(m.gender || "—");
      const matchesBaptized = baptizedFilters.size === 0 || baptizedFilters.has(m.baptism_status ? "Yes" : "No");
      const matchesEducation = educationFilters.size === 0 || educationFilters.has(m.education_status || "—");
      const matchesYearLevel = yearLevelFilters.size === 0 || yearLevelFilters.has((m as any).year_level || "—");
      const matchesEmployment = employmentFilters.size === 0 || employmentFilters.has(m.employment_status || "—");
      const matchesMarital = maritalFilters.size === 0 || maritalFilters.has((m as any).marital_status || "—");
      return matchesSearch && matchesOrg && matchesStatus && matchesGender && matchesBaptized && matchesEducation && matchesYearLevel && matchesEmployment && matchesMarital;
    });

    if (sortColumn && sortDir) {
      result = [...result].sort((a, b) => {
        const aVal = (a as any)[sortColumn] ?? "";
        const bVal = (b as any)[sortColumn] ?? "";
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [scopedMembers, search, orgFilters, statusFilters, genderFilters, baptizedFilters, educationFilters, yearLevelFilters, employmentFilters, maritalFilters, sortColumn, sortDir]);

  const totalMemberPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const adjMemberPage = Math.min(currentPage, totalMemberPages);
  const pagedMembers = filtered.slice((adjMemberPage - 1) * pageSize, adjMemberPage * pageSize);
  const isSearchEmpty = pagedMembers.length === 0 && scopedMembers.length !== 0;
  const emptyRowCount = scopedMembers.length === 0
    ? 0
    : Math.max(0, pageSize - pagedMembers.length - (isSearchEmpty ? 2 : 0));
  const displayRangeStart = filtered.length === 0 ? 0 : (adjMemberPage - 1) * pageSize + 1;
  const displayRangeEnd = adjMemberPage * pageSize;

  const toggleSort = (field: string) => {
    if (sortColumn === field) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") { setSortColumn(""); setSortDir(null); }
    } else {
      setSortColumn(field);
      setSortDir("asc");
    }
  };

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const SortIcon = ({ field, active }: { field: string; active: boolean }) => {
    if (!active) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 transition-opacity group-hover:opacity-70" />;
    }
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 opacity-100" />
      : <ArrowDown className="w-3 h-3 ml-1 opacity-100" />;
  };

  const COLUMN_DEFS: ColumnDef[] = [
    {
      key: "member", label: "Member", sortField: "full_name",
      render: (m) => (
        <div className="flex items-center gap-2">
          <Avatar className="w-7 h-7">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
              {getMemberInitials(m)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm text-foreground">{getMemberDisplayName(m)}</p>
          </div>
        </div>
      ),
    },
    {
      key: "uid", label: "UID", sortField: "id",
      render: (m) => {
        const shortId = typeof m.id === "string" ? m.id.slice(0, 6).toUpperCase() : "------";
        return <span className="text-xs font-mono text-muted-foreground">M-{shortId}</span>;
      },
    },
    {
      key: "email", label: "Email", sortField: "email",
      render: (m) => <span className="text-sm text-muted-foreground">{m.email || "—"}</span>,
    },
    {
      key: "organization", label: "Ministry", sortField: "department",
      render: (m) => m.department ? <Badge variant="secondary" className="font-normal">{m.department}</Badge> : <span className="text-sm text-muted-foreground">—</span>,
    },
    {
      key: "phone", label: "Phone", sortField: "phone",
      render: (m) => <span className="text-sm text-muted-foreground">{m.phone || "—"}</span>,
    },
    {
      key: "gender", label: "Gender", sortField: "gender",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).gender || "—"}</span>,
    },
    {
      key: "status", label: "Status", sortField: "status",
      render: (m) => (
        <Badge variant={m.status === "Active" ? "default" : "secondary"}
          className={
            m.status === "Active"
              ? "border-0 bg-success/10 text-success hover:bg-success/20"
              : "border-0 bg-orange-100 text-orange-700 hover:bg-orange-200"
          }>
          {m.status}
        </Badge>
      ),
    },
    {
      key: "baptized", label: "Baptized", sortField: "baptism_status",
      render: (m) => <span className="text-sm text-muted-foreground">{m.baptism_status ? "Yes" : "No"}</span>,
    },
    {
      key: "date_joined", label: "Date Joined", sortField: "date_joined",
      render: (m) => <span className="text-sm text-muted-foreground">{m.date_joined || "—"}</span>,
    },
    {
      key: "date_of_birth", label: "Birthday", sortField: "date_of_birth",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).date_of_birth || "—"}</span>,
    },
    {
      key: "family", label: "Family", sortField: "family_household",
      render: (m) => <span className="text-sm text-muted-foreground">{m.family_household || "—"}</span>,
    },
    {
      key: "city", label: "City", sortField: "city",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).city || "—"}</span>,
    },
    {
      key: "state", label: "State", sortField: "state",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).state || "—"}</span>,
    },
    {
      key: "zip_code", label: "Zip Code", sortField: "zip_code",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).zip_code || "—"}</span>,
    },
    {
      key: "education_status", label: "Education", sortField: "education_status",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).education_status || "—"}</span>,
    },
    {
      key: "employment_status", label: "Employment", sortField: "employment_status",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).employment_status || "—"}</span>,
    },
    {
      key: "marital_status", label: "Marital Status", sortField: "marital_status",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).marital_status || "—"}</span>,
    },
    {
      key: "address", label: "Address", sortField: "address",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).address || "—"}</span>,
    },
    {
      key: "year_level", label: "Year / Level", sortField: "year_level",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).year_level || "—"}</span>,
    },
    {
      key: "school_name", label: "School", sortField: "school_name",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).school_name || "—"}</span>,
    },
    {
      key: "field_of_study", label: "Field of Study", sortField: "field_of_study",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).field_of_study || "—"}</span>,
    },
    {
      key: "degree_earned", label: "Degree", sortField: "degree_earned",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).degree_earned || "—"}</span>,
    },
    {
      key: "occupation", label: "Occupation", sortField: "occupation",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).occupation || "—"}</span>,
    },
    {
      key: "industry", label: "Industry", sortField: "industry",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).industry || "—"}</span>,
    },
    {
      key: "employer_name", label: "Employer", sortField: "employer_name",
      render: (m) => <span className="text-sm text-muted-foreground">{(m as any).employer_name || "—"}</span>,
    },
    {
      key: "notes", label: "Notes", sortField: "notes",
      render: (m) => <span className="text-sm text-muted-foreground truncate max-w-[200px] block">{(m as any).notes || "—"}</span>,
    },
  ];

  const columns = useMemo(() => {
    const byKey = new Map(COLUMN_DEFS.map((col) => [col.key, col]));
    const ordered = columnOrder
      .map((key) => byKey.get(key))
      .filter((col): col is ColumnDef => Boolean(col));
    const missing = COLUMN_DEFS.filter((col) => !columnOrder.includes(col.key));
    return [...ordered, ...missing];
  }, [columnOrder]);

  const activeColumns = columns.filter((c) => visibleColumns.has(c.key));

  const exportCSV = () => {
    const headers = ["Full Name", "Email", "Phone", "Ministry", "Status", "Gender", "Baptized", "Date Joined", "Birthday", "Family", "Address", "City", "State", "Zip Code", "Marital Status", "Education", "Year/Level", "School", "Field of Study", "Degree", "Employment", "Occupation", "Industry", "Employer", "Notes"];
    const rows = scopedMembers.map((m) => [
      getMemberDisplayName(m), m.email || "", m.phone || "", m.department || "", m.status || "", (m as any).gender || "", m.baptism_status ? "Yes" : "No", m.date_joined || "", (m as any).date_of_birth || "", m.family_household || "", (m as any).address || "", (m as any).city || "", (m as any).state || "", (m as any).zip_code || "", (m as any).marital_status || "", (m as any).education_status || "", (m as any).year_level || "", (m as any).school_name || "", (m as any).field_of_study || "", (m as any).degree_earned || "", (m as any).employment_status || "", (m as any).occupation || "", (m as any).industry || "", (m as any).employer_name || "", (m as any).notes || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Members exported!");
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {(!hideTitle || !hideActions) && (
        <div className={cn("flex items-center gap-3", hideTitle ? "justify-end" : "justify-between")}>
          {!hideTitle && (
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Members</h1>
            </div>
          )}
          {!hideActions && (
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" size="sm" onClick={exportCSV} className="hidden sm:flex">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
              <Button variant="outline" size="icon" onClick={exportCSV} className="sm:hidden h-9 w-9">
                <Download className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => { if (!requireSubscription()) return; setShowAddDialog(true); }} disabled={isAtMemberLimit}>
                {isPreview ? <span className="mr-1"></span> : null}
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <CommunityInsights />

      <div className="stat-card p-0 overflow-visible border-transparent">
      {(() => {
        const scopedOrganizations = peopleScoped && allowedMinistryNames
          ? organizations.filter((o: any) => allowedMinistryNames.has(o.name))
          : organizations;
        const orgNames = sortGeneralFirst(scopedOrganizations.map((o: any) => o.name));
        const orgTypes = [...new Set(scopedOrganizations.map((o: any) => o.type))].sort();
        const groupedOrgs = orgTypes.map(type => ({
          groupLabel: type,
          options: scopedOrganizations.filter((o: any) => o.type === type).map((o: any) => o.name).sort(),
        }));
        const uniqueStatuses = [...new Set(scopedMembers.map(m => m.status || "—"))].sort();
        const uniqueGenders = ["Male", "Female"];
        const uniqueBaptized = ["Yes", "No"];
        const educationOptions = ["Not in School", "Preschool", "Elementary School", "Middle School", "High School", "College / University", "Vocational / Trade School", "Completed / Graduated"];
        const YEAR_LEVELS_BY_STATUS: Record<string, string[]> = {
          "Preschool": ["Pre-K", "Kindergarten"],
          "Elementary School": ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5"],
          "Middle School": ["Grade 6", "Grade 7", "Grade 8"],
          "High School": ["Grade 9", "Grade 10", "Grade 11", "Grade 12"],
          "College / University": ["Freshman", "Sophomore", "Junior", "Senior", "Master's", "PhD"],
          "Vocational / Trade School": ["1st Year", "2nd Year", "3rd Year"],
        };
        const groupedYearLevels = Object.entries(YEAR_LEVELS_BY_STATUS).map(([groupLabel, options]) => ({ groupLabel, options }));
        const allYearLevelOptions = Object.values(YEAR_LEVELS_BY_STATUS).flat();
        const employmentOptions = ["Employed Full-Time", "Employed Part-Time", "Self-Employed", "Unemployed", "Retired"];
        const maritalOptions = ["Single", "Married", "Widowed", "Divorced", "Separated"];
        const filterGroups = [
          { key: "status", label: "Status", options: uniqueStatuses, selected: statusFilters, onToggle: toggleStatusFilter, onClear: () => setStatusFilters(new Set()) },
          { key: "gender", label: "Gender", options: uniqueGenders, selected: genderFilters, onToggle: toggleGenderFilter, onClear: () => setGenderFilters(new Set()) },
          { key: "baptized", label: "Baptized", options: uniqueBaptized, selected: baptizedFilters, onToggle: toggleBaptizedFilter, onClear: () => setBaptizedFilters(new Set()) },
          { key: "marital", label: "Marital Status", options: maritalOptions, selected: maritalFilters, onToggle: toggleMaritalFilter, onClear: () => setMaritalFilters(new Set()) },
          { key: "employment", label: "Employment Status", options: employmentOptions, selected: employmentFilters, onToggle: toggleEmploymentFilter, onClear: () => setEmploymentFilters(new Set()) },
          { key: "year_level", label: "Year / Grade Level", options: allYearLevelOptions, groupedOptions: groupedYearLevels, selected: yearLevelFilters, onToggle: toggleYearLevelFilter, onClear: () => setYearLevelFilters(new Set()) },
          { key: "organization", label: "Ministry", options: orgNames, groupedOptions: groupedOrgs, selected: orgFilters, onToggle: toggleOrgFilter, onClear: () => setOrgFilters(new Set()) },
          { key: "education", label: "Education Status", options: educationOptions, selected: educationFilters, onToggle: toggleEducationFilter, onClear: () => setEducationFilters(new Set()) },
        ].filter((group) => {
          const columnForFilter = FILTER_TO_COLUMN[group.key];
          return columnForFilter ? visibleColumns.has(columnForFilter) : true;
        });

        return (
          <>
          <div className="px-0 py-3 border-b border-transparent relative sm:px-4">
          <TableFilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search members..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterColumns={2}
            filterGroups={filterGroups}
            filterOpen={filterOpen}
            onFilterOpenChange={setFilterOpen}
            expandedGroups={expandedGroups}
            onExpandedGroupsChange={setExpandedGroups}
            leadingControls={
              <div className="flex items-center border border-border rounded-md overflow-hidden sm:rounded-lg">
                <button
                  onClick={() => setViewMode("cards")}
                  className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={cn("p-2 transition-colors", viewMode === "table" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                  title="Table view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            }
            extraControls={
              <div className="flex items-center gap-1.5 sm:gap-2">
                {onAdd && (
                  <Button size="sm" className="h-7 w-7 p-0 text-[11px] sm:h-9 sm:w-auto sm:px-3 sm:text-sm" onClick={onAdd} disabled={isAtMemberLimit}>
                    {isPreview ? <span className="mr-1"></span> : null}
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Add Member</span>
                  </Button>
                )}
              </div>
            }
          />
          </div>
          {columnsOpen && (
            <TableColumnsPanel
              columns={columns.map(c => ({ key: c.key, label: c.label }))}
              visibleColumns={visibleColumns as Set<string>}
              onToggle={(key) => toggleColumn(key as ColumnKey)}
              onReset={() => setVisibleColumns(new Set(DEFAULT_VISIBLE_COLUMNS))}
              onClose={() => setColumnsOpen(false)}
              onReorder={(keys) => setColumnOrder(keys as ColumnKey[])}
            />
          )}
          </>
        );
      })()}

      <div className="min-h-[420px]">
      {isLoading ? (
        <div className="flex min-h-[420px] items-center justify-center text-muted-foreground">Loading members...</div>
      ) : (
        <>
          {/* Card view */}
          <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5", viewMode !== "cards" && "hidden")}>
            {filtered.map((member) => (
              <div
                key={member.id}
                className="stat-card group cursor-pointer active:scale-[0.99] transition-transform"
                onClick={() => setViewMember(member)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="hidden md:flex w-10 h-10">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-sm font-semibold">
                        {getMemberInitials(member)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-foreground hover:underline truncate">{getMemberDisplayName(member)}</p>
                      <p className="text-xs font-mono text-muted-foreground">M-{member.id.slice(0, 6).toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-secondary transition-colors opacity-0 group-hover:opacity-100">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setEditMember(member); }}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setDeleteId(member.id); }} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="hidden md:block space-y-1 text-xs text-muted-foreground mb-3">
                  {member.email && <p className="truncate">{member.email}</p>}
                  {member.phone && <p>{member.phone}</p>}
                  {member.department && <p className="truncate">{member.department}</p>}
                  {(member as any).city && <p className="truncate">{(member as any).city}{(member as any).state ? `, ${(member as any).state}` : ""}</p>}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border/50">
                  <Badge
                    variant={member.status === "Active" ? "default" : "secondary"}
                    className={cn(
                      "text-xs border-0",
                      member.status === "Active"
                        ? "bg-success/10 text-success"
                        : "bg-orange-100 text-orange-700"
                    )}
                  >
                    {member.status || "—"}
                  </Badge>
                  <button
                    onClick={() => setViewMember(member)}
                    className="hidden md:flex text-xs text-primary font-medium items-center gap-1 hover:underline"
                  >
                    View <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Table view */}
          <div className={cn("relative w-full -mx-4 rounded-none overflow-hidden sm:mx-0", viewMode !== "table" && "hidden")}>
            <div className="w-full overflow-x-auto">
            <table className="w-full min-w-full">
              <thead>
                <tr className="bg-gray-200">
                  {activeColumns.map((col) => (
                    <th
                      key={col.key}
                      className="group text-left text-[10px] sm:text-sm font-semibold text-gray-700 tracking-wider px-4 py-2.5 cursor-pointer select-none hover:text-gray-900 transition-colors"
                      onClick={() => toggleSort(col.sortField)}
                    >
                      <span className="inline-flex items-center">
                        {col.label}
                        <SortIcon field={col.sortField} active={sortColumn === col.sortField && !!sortDir} />
                      </span>
                    </th>
                  ))}
                  <th className="w-[8%] text-center text-[10px] sm:text-sm font-semibold text-gray-700 tracking-wider px-2 py-2">
                    <TableSettingsGear
                      columns={columns.map(c => ({ key: c.key, label: c.label }))}
                      visibleColumns={visibleColumns as Set<string>}
                      onToggle={(key) => toggleColumn(key as ColumnKey)}
                      open={columnsOpen}
                      onOpenChange={setColumnsOpen}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedMembers.length === 0 && scopedMembers.length === 0 ? (
                  <tr className="h-[36px]">
                    <td colSpan={activeColumns.length + 1} className="text-center py-16 text-muted-foreground text-sm">
                      No members yet. Add your first member!
                    </td>
                  </tr>
                ) : isSearchEmpty ? (
                  <>
                    <tr className="h-[36px] border-b border-transparent">
                      <td colSpan={activeColumns.length + 1} className="px-4 py-0.5" />
                    </tr>
                    <tr className="h-[36px] border-b border-transparent">
                      <td colSpan={activeColumns.length + 1} className="px-4 py-0.5 text-center text-sm text-muted-foreground">
                        No members match your search.
                      </td>
                    </tr>
                  </>
                ) : (
                  pagedMembers.map((member, idx) => (
                    <tr key={member.id} className={cn("border-b border-transparent transition-colors cursor-pointer h-[36px]", idx % 2 === 0 ? "bg-background" : "bg-muted/20", "hover:bg-primary/10")} onClick={() => setViewMember(member)}>
                      {activeColumns.map((col) => (
                        <td key={col.key} className="px-4 py-0.5 max-w-[200px] truncate text-sm">{col.render(member)}</td>
                      ))}
                      <td className="px-2 py-0.5 text-center w-[8%]" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-secondary transition-colors">
                              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setEditMember(member); }}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setDeleteId(member.id); }} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
                {Array.from({ length: emptyRowCount }).map((_, idx) => (
                  <tr key={`empty-row-${idx}`} className={cn("h-[36px] border-b border-transparent", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td colSpan={activeColumns.length + 1} className="px-4 py-0.5" />
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="flex flex-col items-center gap-2 px-4 py-3 sm:flex-row sm:justify-between sm:gap-0">
              <p className="hidden text-xs text-muted-foreground sm:block">Showing {displayRangeStart}–{displayRangeEnd} of {filtered.length}</p>
              <div className="flex w-full items-center justify-center gap-2 sm:w-auto">
                <span className="hidden text-xs text-muted-foreground sm:block">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                {totalMemberPages > 1 && (
                  <>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjMemberPage === 1} onClick={() => setCurrentPage(1)}>«</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjMemberPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</Button>
                    <span className="text-xs px-2">{adjMemberPage} / {totalMemberPages}</span>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjMemberPage === totalMemberPages} onClick={() => setCurrentPage((p) => p + 1)}>›</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjMemberPage === totalMemberPages} onClick={() => setCurrentPage(totalMemberPages)}>»</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
      </div>

      <MemberDetailDialog open={!!viewMember} onClose={() => setViewMember(null)} member={viewMember} onEdit={(m) => { setViewMember(null); setEditMember(m); }} />
      <MemberFormDialog open={showAddDialog || !!editMember} onClose={() => { setShowAddDialog(false); setEditMember(null); }} member={editMember} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete member?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. This will permanently remove the member record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (!requireSubscription()) return; if (deleteId) deleteMutation.mutate(deleteId); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Members;
